import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    AppBlockPort,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import NodeArgument from '@appblocks/codegeneration/Nodes/NodeArgument';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import * as tios from './tios';
import * as simulator from './simulator';
import * as zephyr from './zephyr';
import * as micropython from './micropython';

export default class CustomFunctionNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Custom Function';
        this.id = 'node_custom';
        this.icon = 'BiCodeCurly';
        this.linkedFeature = 'general';

        const settings: Setting[] = [
            {
                id: 'NAME',
                name: 'Function Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'custom_function',
            },
            {
                id: 'CODE',
                name: 'Code',
                settingType: SettingType.CODE,
                required: true,
                default: 'sys.debugprint(param1)',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): any {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const NAME = this.getSettingValue('NAME');
            return `${NAME}()`;
        }
        return super.getSettingValue(settingId);
    }

    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'FEATURE':
            case 'X':
            case 'Y':
            case 'ARGUMENTS':
                return SettingState.HIDDEN;
            default:
                return super.getState(settingId);
        }
    }

    getInputs(): AppBlockPort[] {
        const INPUTS = this.getSettingValue('INPUTS');
        const inputPorts = [];
        for (let i = 0; i < INPUTS.length; i++) {
            const INPUT = INPUTS[i];
            const params = [];
            for (let j = 0; j < INPUT.getSettingValue('PARAMS').length; j++) {
                const param = INPUT.getSettingValue('PARAMS')[j];
                params.push({
                    name: param.getSettingValue('NAME'),
                    dataType: param.getSettingValue('DATATYPE'),
                    default: '',
                });
            }
            inputPorts.push({
                name: INPUTS[i].getSettingValue('NAME'),
                portId: INPUTS[i].getSettingValue('PORTID'),
                params,
            });
        }
        return inputPorts;
    }

    getOutputs(): AppBlockPort[] {
        const OUTPUTS = this.getSettingValue('OUTPUTS');
        const outputPorts: AppBlockPort[] = [];
        for (let i = 0; i < OUTPUTS.length; i++) {
            const OUTPUT = OUTPUTS[i];
            const params = [];
            for (let j = 0; j < OUTPUT.getSettingValue('PARAMS').length; j++) {
                const param = OUTPUT.getSettingValue('PARAMS')[j];
                params.push({
                    name: param.getSettingValue('NAME'),
                    dataType: param.getSettingValue('DATATYPE'),
                });
            }
            outputPorts.push({
                name: OUTPUTS[i].getSettingValue('NAME'),
                portId: OUTPUTS[i].getSettingValue('PORTID'),
                params,
            });
        }
        return outputPorts;
    }

    validate(): ValidationMessage[] {
        const messages: ValidationMessage[] = [];
        const project: ProjectConfiguration = this.project;
        let INPUTS = this.getSettingValue('INPUTS');
        if (INPUTS.length === 0) {
            const inPort = new NodePort();
            inPort.initSettings();
            inPort.setSettingValue('NAME', 'Run');
            inPort.setSettingValue('PORTID', 'run');
            const param = new NodeParameter();
            param.initSettings();
            param.setSettingValue('NAME', 'param1');
            param.setSettingValue('DATATYPE', TibboDataType.STRING);
            param.setSettingValue('DEFAULT', '');
            inPort.setSettingValue('PARAMS', [param]);
            this.setSettingValue('INPUTS', [inPort]);
            INPUTS = this.getSettingValue('INPUTS');
        }
        this.inputs = this.getInputs();
        const params = this.getInputs()[0].params;
        const ARGUMENTS = this.getSettingValue('ARGUMENTS');
        for (let i = 0; i < params.length; i++) {
            const param = params[i];
            let found = false;
            for (let j = 0; j < ARGUMENTS.length; j++) {
                const arg = ARGUMENTS[j];
                if (arg.getSettingValue('TARGETPARAM') === param.name) {
                    found = true;
                }
            }
            if (!found) {
                const arg = FeatureUtils.getCodeItem('node_argument', project);
                arg.initSettings(project);
                arg.setSettingValue('TARGETPARAM', param.name);
                arg.setSettingValue('ARGVALUE', '');
                ARGUMENTS.push(arg);
            }
        }
        for (let i = 0; i < ARGUMENTS.length; i++) {
            const arg = ARGUMENTS[i];
            let found = false;
            for (let j = 0; j < params.length; j++) {
                const param = params[j];
                if (arg.getSettingValue('TARGETPARAM') === param.name) {
                    found = true;
                }
            }
        }
        let OUTPUTS = this.getSettingValue('OUTPUTS');
        if (OUTPUTS.length === 0) {
            const outPort = new NodePort();
            outPort.initSettings();
            outPort.setSettingValue('NAME', 'Result');
            outPort.setSettingValue('PORTID', 'result');
            const param = new NodeParameter();
            param.initSettings();
            param.setSettingValue('NAME', 'result');
            param.setSettingValue('DATATYPE', TibboDataType.STRING);
            outPort.setSettingValue('PARAMS', [param]);

            this.setSettingValue('OUTPUTS', [outPort]);
            OUTPUTS = this.getSettingValue('OUTPUTS');
        }
        this.outputs = this.getOutputs();
        return messages;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'micropython':
                return micropython.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return `#### Custom Function
Write Tibbo Basic code here to execute custom logic.
`;
    }
}
